import { useRef, useCallback, useMemo, useEffect } from 'react';

const MAX_MOVE_PIXEL = 15;

export default (
  onActive: () => void,
  onInActive: () => void,
  disabled = false,
  duration = 300
) => {
  const startPointRef = useRef({ x: 0, y: 0 });
  const startTime = useRef(0);
  const inActiveTimeout = useRef<NodeJS.Timeout>();

  const handleActive = useCallback(() => {
    startTime.current = Date.now();
    inActiveTimeout.current && clearTimeout(inActiveTimeout.current);
    onActive();
  }, [onActive]);

  const handleInActive = useCallback(() => {
    const leftTime = Math.max(duration - (Date.now() - startTime.current), 0);

    if (leftTime > 0) {
      inActiveTimeout.current = setTimeout(() => {
        onInActive();
      }, leftTime);
    } else {
      onInActive();
    }
  }, [duration, onInActive]);

  useEffect(() => {
    return () => {
      if (inActiveTimeout.current) {
        clearTimeout(inActiveTimeout.current);
      }
    };
  }, []);

  return useMemo(
    () => ({
      onTouchStart: (e: React.TouchEvent) => {
        if (disabled) {
          return;
        }
        startPointRef.current = {
          x: e.changedTouches[0].screenX,
          y: e.changedTouches[0].screenY,
        };
        handleActive();
      },
      onTouchEnd: () => {
        if (disabled) {
          return;
        }
        handleInActive();
      },
      onTouchCancel: () => {
        if (disabled) {
          return;
        }
        handleInActive();
      },
      onTouchMove: (e: React.TouchEvent) => {
        if (startPointRef.current) {
          if (
            Math.abs(e.changedTouches[0].screenX - startPointRef.current.x) >
              MAX_MOVE_PIXEL ||
            Math.abs(e.changedTouches[0].screenY - startPointRef.current.y) >
              MAX_MOVE_PIXEL
          ) {
            handleInActive();
          }
        }
      },

      onMouseDown: (e: React.MouseEvent) => {
        if (disabled) {
          return;
        }
        startPointRef.current = {
          x: e.clientX,
          y: e.clientY,
        };
        startTime.current = Date.now();
        handleActive();
      },
      onMouseUp: () => {
        if (disabled) {
          return;
        }
        handleInActive();
      },
      onMouseLeave: () => {
        if (disabled) {
          return;
        }
        handleInActive();
      },
      draggable: false,
    }),
    [disabled, handleActive, handleInActive]
  );
};
